import { Stage, UserJourney, ContentItem, Activity } from '@/types/journey';

// Helper function to create sample activities
const createSampleActivities = (items: string[]): Activity[] => {
  return items.map((text, index) => ({
    id: `default-activity-${index}`,
    text
  }));
};

export const DEFAULT_STAGES: Stage[] = [
  {
    id: '1',
    title: 'Problem Recognition',
    description: 'The design lead recognizes the need for better documentation of user research findings',
    painPoints: 'Current tools are fragmented, findings are scattered across different formats and platforms',
    opportunities: 'Create an all-in-one solution for documenting and visualizing user journeys',
    emotion: -1,
    quote: 'I need a better way to document and share all these user insights we\'re collecting',
    contentItems: [
      {
        id: 'content-description-1',
        type: 'description',
        content: 'The design lead recognizes the need for better documentation of user research findings'
      },
      {
        id: 'content-activities-1',
        type: 'activities',
        content: createSampleActivities([
          'Review existing research documentation',
          'Identify gaps in current tools',
          'Research available solutions'
        ])
      },
      {
        id: 'content-painPoints-1',
        type: 'painPoints',
        content: 'Current tools are fragmented, findings are scattered across different formats and platforms'
      },
      {
        id: 'content-opportunities-1',
        type: 'opportunities',
        content: 'Create an all-in-one solution for documenting and visualizing user journeys'
      }
    ]
  },
  {
    id: '2',
    title: 'Discovery',
    description: 'The design lead discovers the Journey Map Notebook through a colleague\'s recommendation',
    painPoints: 'Initially skeptical if it will handle all types of user research findings',
    opportunities: 'Showcase versatility and ease of use in marketing materials, leverage word-of-mouth',
    emotion: 0,
    quote: "This Journey Map Notebook tool seems interesting, but will it be flexible enough for our needs?",
    contentItems: [
      {
        id: 'content-description-2',
        type: 'description',
        content: 'The design lead discovers the Journey Map Notebook through a colleague\'s recommendation'
      },
      {
        id: 'content-activities-2',
        type: 'activities',
        content: createSampleActivities([
          'Ask colleagues for recommendations',
          'Browse through online reviews',
          'Compare different mapping tools'
        ])
      },
      {
        id: 'content-touchPoints-2',
        type: 'touchPoints',
        content: [
          { id: 'tp-1', icon: '📱', name: 'Mobile App' },
          { id: 'tp-2', icon: '🌐', name: 'Website' }
        ]
      },
      {
        id: 'content-painPoints-2',
        type: 'painPoints',
        content: 'Initially skeptical if it will handle all types of user research findings'
      },
      {
        id: 'content-opportunities-2',
        type: 'opportunities',
        content: 'Showcase versatility and ease of use in marketing materials, leverage word-of-mouth'
      }
    ]
  },
  {
    id: '3',
    title: 'First Use',
    description: 'The design lead tries the tool to document recent user research findings',
    painPoints: 'Brief learning curve to understand all available features',
    opportunities: 'Provide intuitive onboarding tour, sample templates for quick starts',
    emotion: 1,
    quote: 'Wow, this is actually easier than I expected and covers everything we need!',
    contentItems: [
      {
        id: 'content-description-3',
        type: 'description',
        content: 'The design lead tries the tool to document recent user research findings'
      },
      {
        id: 'content-activities-3',
        type: 'activities',
        content: createSampleActivities([
          'Sign up for the tool',
          'Start a new journey map',
          'Add first research findings'
        ])
      },
      {
        id: 'content-painPoints-3',
        type: 'painPoints',
        content: 'Brief learning curve to understand all available features'
      },
      {
        id: 'content-opportunities-3',
        type: 'opportunities',
        content: 'Provide intuitive onboarding tour, sample templates for quick starts'
      }
    ]
  },
  {
    id: '4',
    title: 'Regular Use',
    description: 'The design lead incorporates the tool into their regular workflow for documenting user experiences',
    painPoints: 'Wishes for more team collaboration features for simultaneous editing',
    opportunities: 'Develop collaboration features, commenting, and version control',
    emotion: 2,
    quote: 'This has completely transformed how we document and communicate user journeys across the team',
    contentItems: [
      {
        id: 'content-description-4',
        type: 'description',
        content: 'The design lead incorporates the tool into their regular workflow for documenting user experiences'
      },
      {
        id: 'content-activities-4',
        type: 'activities',
        content: createSampleActivities([
          'Create journey maps for each project',
          'Share maps with team members',
          'Use in presentations with stakeholders'
        ])
      },
      {
        id: 'content-touchPoints-4',
        type: 'touchPoints',
        content: [
          { id: 'tp-3', icon: '📱', name: 'Mobile App' },
          { id: 'tp-4', icon: '🌐', name: 'Website' },
          { id: 'tp-5', icon: '📧', name: 'Email' }
        ]
      },
      {
        id: 'content-painPoints-4',
        type: 'painPoints',
        content: 'Wishes for more team collaboration features for simultaneous editing'
      },
      {
        id: 'content-opportunities-4',
        type: 'opportunities',
        content: 'Develop collaboration features, commenting, and version control'
      }
    ]
  }
];

export const STORAGE_KEY = 'journey-map-stages';
export const JOURNEY_KEY = 'journey-map-data';

export const DEFAULT_JOURNEY: UserJourney = {
  userName: 'Sarah Johnson',
  userRole: 'Design Lead at a medium-sized software company',
  userPhoto: null,
  scenario: 'Sarah is looking for a better way to document and communicate user research findings with her cross-functional team.',
  goal: 'Find and implement a tool that lets her create visual journey maps that capture all aspects of user research and can be easily shared.',
  mapName: 'Journey Map Notebook - User Experience',
  insights: {
    gaps: 'Current documentation tools are fragmented and don\'t provide a holistic view of the user journey. Research findings are scattered across multiple platforms making it difficult to see patterns and communicate insights effectively.',
    opportunities: 'Create an all-in-one solution specifically designed for UX professionals that combines the best aspects of journey mapping with rich documentation capabilities and easy sharing options.'
  },
  insightItems: [
    {
      id: 'insight-opportunities-default',
      type: 'opportunities',
      content: [
        {
          id: 'opportunity-default-1',
          text: 'Create an all-in-one solution specifically designed for UX professionals'
        },
        {
          id: 'opportunity-default-2',
          text: 'Combine the best aspects of journey mapping with rich documentation capabilities'
        },
        {
          id: 'opportunity-default-3',
          text: 'Add easy sharing options for cross-functional teams'
        }
      ]
    },
    {
      id: 'insight-internalOwnership-default',
      type: 'internalOwnership',
      content: [
        {
          id: 'ownership-default-1',
          team: 'UX Design',
          task: 'Create intuitive interface for journey mapping'
        },
        {
          id: 'ownership-default-2',
          team: 'Development',
          task: 'Ensure responsive design across all devices'
        },
        {
          id: 'ownership-default-3',
          team: 'Customer Support',
          task: 'Provide timely responses to user questions'
        }
      ]
    },
    {
      id: 'insight-successMetrics-default',
      type: 'successMetrics',
      content: [
        {
          id: 'metric-default-1',
          text: 'Increase in documentation efficiency by 30%'
        },
        {
          id: 'metric-default-2',
          text: 'Positive feedback from 80% of team members'
        },
        {
          id: 'metric-default-3',
          text: 'Reduction in onboarding time for new team members'
        }
      ]
    }
  ]
};
